import {Component} from "@theme/component";
import {QuickAddComponent} from "@theme/quick-add";
import {isClickedOutside, isMobileBreakpoint, isTouchDevice} from "@theme/utilities";

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLButtonElement} trigger - The button element.
 * @property {HTMLAnchorElement} productLink - The product link element.
 *
 * @extends Component<Refs>
 */

export class ProductHotspotComponent extends Component {
  requiredRefs = ["trigger", "dialog"];

  connectedCallback() {
    super.connectedCallback();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  /**
   * Open the quick-add modal
   * @returns {void}
   */
  #openQuickAddModal() {
    const quickAddComponent = /** @type {QuickAddComponent | null} */ (this.querySelector("quick-add-component"));

    if (!quickAddComponent) return;
    quickAddComponent.handleClick(new MouseEvent("click", {bubbles: true, cancelable: true}));
  }

  /**
   * Calculate the placement of the dialog.
   * @returns {Promise<void> | undefined}
   */
  #calculateDialogPlacement() {
    const {trigger, dialog} = this.refs;

    const hotspotsContainer = this.parentElement;

    if (!hotspotsContainer) {
      return;
    }

    // Spacing constants
    const BUTTON_GAP = 10; // Gap between button and dialog
    const CONTAINER_GAP = 10; // Gap from container edges
    const TOTAL_GAP = BUTTON_GAP + CONTAINER_GAP;

    // Get container bounds
    const containerRect = hotspotsContainer?.getBoundingClientRect();

    // Get button dimensions
    const triggerRect = trigger.getBoundingClientRect();

    // To get dialog dimensions, we need to temporarily show it invisibly
    // Show dialog invisibly to measure it
    dialog.style.visibility = "hidden";
    dialog.style.display = "block";
    dialog.style.transform = "none";
    dialog.removeAttribute("data-placement");

    const {width: dialogWidth, height: dialogHeight} = dialog.getBoundingClientRect();

    // Reset dialog state
    dialog.style.removeProperty("display");
    dialog.style.removeProperty("visibility");
    dialog.style.removeProperty("transform");
    // Calculate button position relative to container
    const buttonLeft = triggerRect.left - containerRect.left;
    const buttonRight = triggerRect.right - containerRect.left;
    const buttonTop = triggerRect.top - containerRect.top;
    const buttonBottom = triggerRect.bottom - containerRect.top;

    // Calculate available space
    const spaceRight = containerRect.width - buttonRight - CONTAINER_GAP;
    const spaceLeft = buttonLeft - CONTAINER_GAP;

    // Determine horizontal placement
    let x = "right";

    if (spaceRight >= dialogWidth + BUTTON_GAP) {
      x = "right";
    } else if (spaceLeft >= dialogWidth + BUTTON_GAP) {
      x = "left";
    } else {
      x = "center";
    }

    // Determine vertical placement
    let y = "bottom";
    let verticalOffset = 0;

    if (x !== "center") {
      let dialogStartY = buttonTop; // Default to top-aligned
      let dialogEndY = buttonTop + dialogHeight;

      if (dialogEndY > containerRect.height - CONTAINER_GAP) {
        // If top-aligned overflows bottom
        dialogStartY = buttonBottom - dialogHeight;
        dialogEndY = buttonBottom;
        y = "top";

        if (dialogStartY < CONTAINER_GAP) {
          // If bottom-aligned overflows top
          verticalOffset = CONTAINER_GAP - dialogStartY;
        } else if (dialogEndY > containerRect.height - CONTAINER_GAP) {
          // If bottom-aligned overflows bottom
          verticalOffset = -(dialogEndY - (containerRect.height - CONTAINER_GAP));
        }
      } else {
        if (dialogStartY < CONTAINER_GAP) {
          // If top-aligned overflows top
          if (dialogStartY < CONTAINER_GAP) {
            verticalOffset = CONTAINER_GAP - dialogStartY;
          }
          y = "bottom";
        }
      }
    } else {
      // For center horizontal: position below or above button
      if (containerRect.height - buttonBottom >= dialogHeight + TOTAL_GAP) {
        y = "bottom";
      } else if (buttonTop >= dialogHeight + TOTAL_GAP) {
        y = "top";
      } else {
        // If neither fits well, choose based on button position
        y = buttonTop < containerRect.height / 2 ? "bottom" : "top";
      }
    }

    // Set placement data attribute
    dialog.dataset.placement = `${x},${y}`;

    // Apply vertical offset if needed to keep dialog in bounds
    if (verticalOffset !== 0) {
      dialog.style.setProperty("--dialog-vertical-offset", `${verticalOffset}px`);
    } else {
      dialog.style.removeProperty("--dialog-vertical-offset");
    }

    // Return a promise that resolves after a few ticks to ensure styles are applied
    return new Promise((resolve) => setTimeout(resolve, 100));
  }

  /**
   * Close any other open hotspot dialogs in the document.
   * @returns {void}
   */
  #closeOtherHotspots() {
    const allHotspots = document.querySelectorAll("product-hotspot-component");
    allHotspots.forEach((hotspot) => {
      if (hotspot !== this && hotspot.refs?.dialog?.open) {
        hotspot.closeDialog();
      }
    });
  }

  /**
   * Get the product link for the hotspot product.
   * @returns {HTMLAnchorElement | null} The product link or null.
   */
  getHotspotProductLink() {
    return this.refs.productLink || null;
  }

  /**
   * Handle hotspot click.
   * On mobile/touch devices: opens quick-add modal.
   * On desktop: toggles the dialog (opens if closed, closes if open).
   * Only one hotspot dialog can be open at a time.
   * @param {MouseEvent} e - The click event
   * @returns {void}
   */
  handleHotspotClick = (e) => {
    // Check if it's a touch device (tablets) or mobile breakpoint
    if (isMobileBreakpoint() || isTouchDevice()) {
      e.preventDefault();
      e.stopPropagation();
      this.#openQuickAddModal();
    } else {
      const {dialog} = this.refs;
      if (dialog.open) {
        this.closeDialog();
      } else {
        // Close any other open hotspots before opening this one
        this.#closeOtherHotspots();
        this.showDialog();
      }
    }
  };

  showDialog = async () => {
    const {dialog} = this.refs;
    await this.#calculateDialogPlacement();
    dialog.dataset.showing = "true";
    dialog.show();
    document.body.addEventListener("click", this.lightDismissMouse);
    document.body.addEventListener("keydown", this.lightDismissKeyboard);
    document.body.addEventListener("keyup", this.lightDismissKeyboard);
  };

  /**
   * Close the dialog.
   * @returns {Promise<void>}
   */
  closeDialog = async () => {
    const {dialog} = this.refs;
    dialog.dataset.closing = "true";
    dialog.close();
    document.body.removeEventListener("click", this.lightDismissMouse);
    document.body.removeEventListener("keydown", this.lightDismissKeyboard);
    document.body.removeEventListener("keyup", this.lightDismissKeyboard);
    // we need to use a data-attribute to keep transition-behavior working only when open
    const animations = dialog.getAnimations({subtree: true});
    await Promise.allSettled(animations.map((a) => a.finished));
    if (!dialog.open) {
      delete dialog.dataset.showing;
      delete dialog.dataset.closing;
      delete dialog.dataset.placement;
    }
  };

  /**
   * Light dismiss the dialog.
   * @param {MouseEvent} event - The event.
   * @returns {void}
   */
  lightDismissMouse = (event) => {
    const {dialog} = this.refs;
    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  };

  /**
   * Light dismiss the dialog.
   * @param {KeyboardEvent} event - The event.
   * @returns {void}
   */
  lightDismissKeyboard = (event) => {
    const {dialog} = this.refs;
    if ((event.type === "keydown" && event.key === "Escape") || (event.type === "keyup" && !dialog.matches(":is(:focus, :focus-visible, :focus-within)"))) {
      this.closeDialog();
    }
  };
}

// Register custom element
customElements.define("product-hotspot-component", ProductHotspotComponent);
